import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map, pluck, tap } from 'rxjs/operators';
import { FracStage, FracStageMesh } from '~lmo/models/frac.model';
import { LmoFracsService } from '~lmo/services';
import { trackById } from '~utilities/trackById';

interface FracStageMeshRecord extends FracStage {
  meshRecord: Record<number, FracStageMesh>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sa-lmo-frac-detail-stages',
  styleUrls: ['./lmo-frac-detail-stages.component.scss'],
  templateUrl: './lmo-frac-detail-stages.component.html',
})
export class LmoFracDetailStagesComponent implements OnInit {
  private fracStages$: Observable<FracStage[]>;
  public trackById = trackById;
  public completedFracStages$: Observable<FracStage[]>;
  public completedFracStagesMeshRecord$: Observable<FracStageMeshRecord[]>;
  public totals$: Observable<FracStageMesh[]>;
  public meshes$: Observable<FracStageMesh[]>;
  public displayedColumns$: Observable<string[]>;
  public summaryColumns: number;
  public detailColumns: number;
  constructor(private fracService: LmoFracsService) {}

  public ngOnInit() {
    this.setupFracStages();
    this.setupCompletedFracStages();
    this.setupTotals();

    this.displayedColumns$ = this.totals$.pipe(
      // map((totals) => ['stageNumber', 'completedTime']),
      map((totals) => ['stageNumber', 'completedTime', ...totals.map((mesh) => mesh.type)]),
    );

    this.totals$.subscribe((result) => {
      this.summaryColumns = result.length + 1;
      this.detailColumns = result.length + 2;
    });
  }

  private setupFracStages() {
    this.fracStages$ = this.fracService.currentFrac$.pipe(
      filter((frac) => !!frac),
      pluck('fracStages'),
      map((stages: FracStage[]) => {
        if (stages && stages.length) {
          return stages;
        }
        return [];
      }),
    );
  }

  private setupCompletedFracStages() {
    this.completedFracStages$ = this.fracStages$.pipe(
      map((stages) => stages.filter((stage) => !!stage.completedTimestamp)),
    );
    this.completedFracStagesMeshRecord$ = this.completedFracStages$.pipe(
      map((stages) => {
        return stages.reduce((array: FracStageMeshRecord[], stage) => {
          const meshRecord = stage.meshes.reduce((record, mesh) => {
            record[mesh.id] = mesh;
            return record;
          }, {});
          array.push({
            ...stage,
            meshRecord,
          });
          return array;
        }, []);
      }),
      tap((stages) => {
        stages.sort((a, b) => (a.stageNumber < b.stageNumber ? 1 : -1));
      }),
    );
  }

  private setupTotals() {
    this.totals$ = this.fracStages$.pipe(
      map((stages) => {
        const sums: Record<number, FracStageMesh> = stages.reduce((accum: Record<number, FracStageMesh>, stage) => {
          stage.meshes.forEach((stageMesh) => {
            if (!accum[stageMesh.id]) {
              accum[stageMesh.id] = {
                actualVolume: 0,
                id: stageMesh.id,
                preferredOrder: stageMesh.preferredOrder,
                type: stageMesh.type,
                volume: 0,
              };
            }
            const mesh = accum[stageMesh.id];
            mesh.actualVolume += stageMesh.actualVolume || 0;
            mesh.volume += stageMesh.volume || 0;
          });
          return accum;
        }, {});
        const asArray = recordToArray(sums);
        asArray.sort(sortByPreferredOrder);
        return asArray;
      }),
    );
  }

  public asFracStageMeshRecord(data: any): FracStageMeshRecord {
    return data as FracStageMeshRecord;
  }

  public getMeshPumpedVolume(row: FracStageMeshRecord, meshId: number): number {
    if (row && row.meshRecord && row.meshRecord[meshId]) {
      return row.meshRecord[meshId].actualVolume || 0;
    }
    return 0;
  }
}

function sortByPreferredOrder(a: FracStageMesh, b: FracStageMesh) {
  return a.preferredOrder < b.preferredOrder ? -1 : 1;
}

function recordToArray<T>(record: Record<any, T>): T[] {
  return Object.keys(record).map((key) => record[key]);
}
